import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Link,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { roleHome } from '../lib/roleHome';
import { useAuth } from '../contexts/AuthContext';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { GoogleAuthButton } from '../components/auth/GoogleAuthButton';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    fontSize: 14,
    bgcolor: '#FAFBFD',
    '& fieldset': { borderColor: '#E1E8ED', borderWidth: '1.5px' },
    '&:hover fieldset': { borderColor: '#C1C9D2' },
    '&.Mui-focused fieldset': { borderColor: '#0066CC', borderWidth: '2px' },
    '&.Mui-focused': { bgcolor: '#fff' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#0066CC' },
};

export const Login: React.FC = () => {
  const { t } = useTranslation();
  const { user, login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [loginError, setLoginError] = useState<unknown>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const safeRedirect = (raw: string | null): string | null => {
    if (!raw) return null;
    if (!raw.startsWith('/') || raw.startsWith('//')) return null;
    return raw;
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  // Redirect already-authenticated users by role. useEffect (not an early return)
  // so all hooks above are called unconditionally on every render.
  useEffect(() => {
    if (!user) return;
    navigate(roleHome(user.role), { replace: true });
  }, [user, navigate]);

  const onSubmit = async (data: LoginFormData) => {
    setLoginError(null);
    setIsSubmitting(true);
    try {
      const user = await login(data.email, data.password);
      const requested = safeRedirect(params.get('redirect'));
      navigate(requested ?? roleHome(user.role), { replace: true });
    } catch (err: unknown) {
      setLoginError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSuccess = async (idToken: string) => {
    try {
      const u = await loginWithGoogle(idToken);
      const requested = safeRedirect(params.get('redirect'));
      navigate(requested ?? roleHome(u.role), { replace: true });
    } catch (err) {
      setLoginError(err);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        bgcolor: '#FFFFFF',
        '@keyframes fadeUp': {
          from: { opacity: 0, transform: 'translateY(22px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        '@keyframes fadeIn': {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      {/* ── Left brand panel (desktop only) ── */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          width: '44%',
          flexShrink: 0,
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'relative',
          overflow: 'hidden',
          bgcolor: '#0D1B35',
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.055) 1px, transparent 1px)',
          backgroundSize: '22px 22px',
          p: '48px 44px',
          animation: 'fadeIn 500ms ease both',
        }}
      >
        {/* Logo */}
        <Box>
          <img
            src="/logo-full.svg"
            alt="Lysi-Web"
            style={{ height: 46, width: 'auto', filter: 'brightness(0) invert(1)', opacity: 0.92 }}
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        </Box>

        {/* Tagline statement */}
        <Box>
          <Typography variant="h1" sx={{ color: '#FFFFFF', mb: 2.5 }}>
            {t('app.tagline')}
          </Typography>
          {/* Gradient accent rule */}
          <Box
            sx={{
              width: 52,
              height: 3,
              borderRadius: 2,
              background: 'linear-gradient(90deg, #0066CC 0%, #00A86B 100%)',
              mb: 3,
            }}
          />
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.45)',
              fontSize: 13,
              fontWeight: 400,
              lineHeight: 1.65,
              maxWidth: 290,
            }}
          >
            Catálogo farmacéutico, prescripciones electrónicas y gestión clínica en una sola plataforma.
          </Typography>
        </Box>

        {/* DR flag + region */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#002D62' }} />
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#CE1126' }} />
          </Box>
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.3)',
              fontSize: 10.5,
              letterSpacing: '1.2px',
              textTransform: 'uppercase',
              fontWeight: 600,
            }}
          >
            República Dominicana
          </Typography>
        </Box>

        {/* Decorative circles */}
        <Box sx={{ position: 'absolute', bottom: -150, right: -110, width: 420, height: 420, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.055)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -50, right: 50, width: 230, height: 230, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.09)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', top: -70, right: -50, width: 200, height: 200, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.055)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', top: 100, right: -20, width: 80, height: 80, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.08)', pointerEvents: 'none' }} />
      </Box>

      {/* ── Right form panel ── */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          bgcolor: '#FFFFFF',
        }}
      >
        {/* Language switcher */}
        <Box sx={{ position: 'absolute', top: 18, right: 18, zIndex: 10 }}>
          <LanguageSwitcher />
        </Box>

        {/* Mobile brand strip */}
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            bgcolor: '#0D1B35',
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.055) 1px, transparent 1px)',
            backgroundSize: '18px 18px',
            px: 3,
            py: 2,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src="/logo-full.svg"
            alt="Lysi-Web"
            style={{ height: 40, width: 'auto', filter: 'brightness(0) invert(1)', opacity: 0.92 }}
          />
        </Box>

        {/* Centered form */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 3, sm: 5, md: 7, lg: 9 },
            py: { xs: 4, md: 6 },
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: 400,
              animation: 'fadeUp 500ms cubic-bezier(0.22, 1, 0.36, 1) both',
              animationDelay: '100ms',
            }}
          >
            {/* Heading */}
            <Box sx={{ mb: 4.5 }}>
              <Typography variant="h2" sx={{ color: '#0D1B35', mb: 0.75 }}>
                Bienvenido
              </Typography>
              <Typography sx={{ color: '#A0AEC0', fontSize: 14, fontWeight: 400 }}>
                Inicia sesión para continuar
              </Typography>
            </Box>

            {loginError != null && (() => {
              const data = (loginError as any)?.response?.data;
              const status = (loginError as any)?.response?.status;
              if (data?.code === 'EMAIL_NOT_VERIFIED') {
                return (
                  <Alert severity="warning" sx={{ mb: 3, borderRadius: '10px', fontSize: 13 }}>
                    {data.message}{' '}
                    <Link component={RouterLink} to="/verify-email">Reenviar enlace</Link>
                  </Alert>
                );
              }
              if (status === 403) {
                return (
                  <Alert severity="error" sx={{ mb: 3, borderRadius: '10px', fontSize: 13 }}>
                    Esta cuenta no puede usar Google. Usa tu correo y contraseña.
                  </Alert>
                );
              }
              return (
                <Alert severity="error" sx={{ mb: 3, borderRadius: '10px', fontSize: 13 }}>
                  {data?.message ?? 'Error al iniciar sesión.'}
                </Alert>
              );
            })()}

            <GoogleAuthButton onSuccess={handleGoogleSuccess} />

            <Box
              component="form"
              onSubmit={handleSubmit(onSubmit)}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <TextField
                fullWidth
                id="email"
                label={t('auth.email')}
                autoComplete="email"
                autoFocus
                error={!!errors.email}
                helperText={errors.email?.message}
                {...register('email')}
                sx={inputSx}
              />

              <TextField
                fullWidth
                id="password"
                label={t('auth.password')}
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                error={!!errors.password}
                helperText={errors.password?.message}
                {...register('password')}
                sx={inputSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((p) => !p)}
                        edge="end"
                        size="small"
                        sx={{ color: '#A0AEC0', mr: -0.5 }}
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Forgot password */}
              <Box sx={{ textAlign: 'right', mt: -0.5 }}>
                <Button
                  component={RouterLink}
                  to="/forgot-password"
                  variant="text"
                  size="small"
                  sx={{
                    color: '#0066CC',
                    fontSize: 12,
                    fontWeight: 500,
                    textTransform: 'none',
                    p: 0,
                    minWidth: 0,
                    lineHeight: 1,
                    '&:hover': { bgcolor: 'transparent', color: '#004C99' },
                  }}
                >
                  {t('auth.forgotPassword')}
                </Button>
              </Box>

              {/* Submit */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  mt: 0.5,
                  py: 1.75,
                  borderRadius: '10px',
                  fontSize: 14,
                  fontWeight: 700,
                  letterSpacing: '0.3px',
                  bgcolor: '#0066CC',
                  boxShadow: '0 4px 16px rgba(0,102,204,0.30)',
                  transition: 'all 150ms ease',
                  '&:hover': {
                    bgcolor: '#0059B3',
                    boxShadow: '0 6px 22px rgba(0,102,204,0.42)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': { transform: 'translateY(0)', boxShadow: '0 2px 8px rgba(0,102,204,0.25)' },
                  '&.Mui-disabled': { bgcolor: '#E1E8ED', boxShadow: 'none' },
                }}
              >
                {isSubmitting ? t('auth.loggingIn') : t('auth.login')}
              </Button>
            </Box>

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                ¿No tienes cuenta?{' '}
                <Link component={RouterLink} to="/signup" sx={{ color: '#0066CC' }}>
                  Regístrate aquí
                </Link>
              </Typography>
            </Box>

            <Typography
              sx={{ color: '#D1D8E0', textAlign: 'center', mt: 5, fontSize: 11 }}
            >
              {t('footer.copyright')}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
